/* eslint-disable @typescript-eslint/no-explicit-any */
import { BOOK_PAGE_CONTEXT_COMPONENT_IDS, canUseDOM, constants } from '../../modules/utils';
import { TripBookingDataInfo } from '../UpcomingTrips/TripDetails/TripDetails.types';
import { UpdateCancelModalBookPageContextParams, OrderByOrderIdProps } from './CancelTripModal.types';

type MoneyAmount = {
  amount: number;
  currency: string;
  decimalPoint: number;
};

type NormalizedNotification = {
  deliveryMethod: string;
  recipients: unknown[];
};

// Return a trimmed string if the input is a string, otherwise an empty string
const toNonEmptyString = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmedValue = value.trim();
  return trimmedValue || '';
};

// Coerce a value to a finite number, returning 0 for invalid inputs
const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
// Simple boolean normalizer (true -> true, everything else -> false)
const toBoolean = (value: unknown): boolean => value === true;
// Extract a primary rateDetails object from various possible shapes
const getPrimaryRateDetails = (productLike: any): Record<string, any> => {
  const rateDetails = productLike?.rateDetails;
  if (Array.isArray(rateDetails)) {
    const normalizedRateDetails = rateDetails.filter((detail: unknown): detail is Record<string, any> =>
      Boolean(detail && typeof detail === 'object')
    );
    const selected =
      normalizedRateDetails.find(detail => {
        return Boolean(
          detail?.[0]?.isRedemption || detail?.[0]?.isFna || detail?.[0]?.isFNA || detail?.[0]?.isFreeNight
        );
      }) ??
      normalizedRateDetails[0] ??
      {};
    return {
      ...selected,
      0: selected,
    };
  }
  if (rateDetails && typeof rateDetails === 'object') {
    return {
      ...(rateDetails as Record<string, any>),
      0: rateDetails as Record<string, any>,
    };
  }
  return {};
};

// Convert a minor currency amount to its major amount using decimalPoint
const toMonetaryMajorAmount = (amount: unknown, decimalPoint: unknown): number => {
  const parsedAmount = toNumber(amount);
  const parsedDecimalPoint = Number(decimalPoint);

  if (!Number.isFinite(parsedDecimalPoint) || parsedDecimalPoint <= 0) {
    return parsedAmount;
  }

  const normalizedDecimalPoint = Math.trunc(parsedDecimalPoint);
  const divisor = Math.pow(10, normalizedDecimalPoint);
  if (!Number.isFinite(divisor) || divisor === 0) {
    return parsedAmount;
  }
  return Number((parsedAmount / divisor).toFixed(normalizedDecimalPoint));
};

// Map a money-like object into a standardized MoneyAmount
const mapMoneyAmount = (moneyLike: any): MoneyAmount => {
  const decimalPoint = Math.max(0, Math.trunc(toNumber(moneyLike?.decimalPoint)));
  return {
    amount: toMonetaryMajorAmount(moneyLike?.amount, decimalPoint),
    currency: typeof moneyLike?.currency == 'string' ? moneyLike.currency : '',
    decimalPoint,
  };
};

// Ensure recipients is returned as a non-null array
const normalizeRecipients = (recipients: unknown): unknown[] => {
  if (Array.isArray(recipients)) {
    return recipients.filter(recipient => recipient != null);
  }
  return recipients == null ? [] : [recipients];
};

// Normalize notification payloads into a predictable array shape
const normalizeNotifications = (notifications: unknown): NormalizedNotification[] => {
  const notificationList = Array.isArray(notifications)
    ? notifications
    : notifications && typeof notifications === 'object'
    ? [notifications]
    : [];
  return notificationList
    .filter((notification): notification is Record<string, unknown> => {
      return Boolean(notification && typeof notification == 'object');
    })
    .map(notification => ({
      ...notification,
      deliveryMethod: typeof notification['deliveryMethod'] === 'string' ? notification['deliveryMethod'] : '',
      recipients: normalizeRecipients(notification['recipients']),
    }));
};

// Resolve the rate name from various product shapes and fallbacks
const getRateName = (product: {
  rate?: {
    name?: string;
  };
  rates?: {
    name: string;
    localizedName?: {
      translatedText?: string;
    };
  };
}): string => {
  const directRateName = typeof product?.rate?.name === 'string' ? product.rate.name.trim() : '';
  if (directRateName) {
    return directRateName;
  }
  const rates = product?.rates;
  const rateName = typeof rates?.name === 'string' ? rates.name.trim() : '';
  if (rateName) {
    return rateName;
  }
  return typeof rates?.localizedName?.translatedText == 'string' ? rates.localizedName.translatedText.trim() : '';
};

// Derive an end date string (YYYY-MM-DD) from a start date and length of stay
const deriveEndDate = (startDate = '', lengthOfStay = 0): string => {
  if (!startDate || !Number.isFinite(lengthOfStay) || lengthOfStay <= 0) {
    return '';
  }
  const parsed = new Date(startDate);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  parsed.setUTCDate(parsed.getUTCDate() + lengthOfStay);

  return parsed.toISOString().split('T')[0];
};
// Helpers to extract numeric pricing/points values from a segment
const getSegmentTaxAmount = (segment: any): number => {
  return toNumber(segment?.product?.totalPricing?.taxes?.amount);
};
const getSegmentTotalAmount = (segment: any): number => {
  return toNumber(segment?.product?.totalPricing?.total?.amount);
};
const getPerNightAmount = (segment: any): number => {
  const rateDetails = getPrimaryRateDetails(segment?.product);
  return toNumber(rateDetails?.[0]?.perNightAmount?.amount ?? rateDetails?.[0]?.rateAmounts?.amount?.origin?.amount);
};
const getExplicitSegmentPoints = (segment: any): number => {
  const rateDetails = getPrimaryRateDetails(segment?.product);

  return toNumber(rateDetails?.[0]?.mappedPoints ?? rateDetails?.[0]?.points ?? segment?.product?.totalPricing?.points);
};

// Compute points for an item using several potential fields
const getItemPoints = (item: any): number => {
  return toNumber(
    item?.totalPricing?.points ??
      item?.productType?.totalPricing?.points ??
      item?.rateDetails?.mappedPoints ??
      item?.rateDetails?.points
  );
};

// Distribute item points across redemption segments, preserving explicit allocations
const allocatePointsBySegment = (item: any, productSegments: any[]): number[] => {
  if (!productSegments.length) {
    return [getItemPoints(item)];
  }

  const redemptionIndexes = productSegments.reduce<number[]>((indexes, segment, index) => {
    const rateDetails = getPrimaryRateDetails(segment?.product);
    if (toBoolean(rateDetails?.[0]?.isRedemption)) {
      indexes.push(index);
    }
    return indexes;
  }, []);

  if (!redemptionIndexes.length) {
    return productSegments.map(() => 0);
  }

  const allocatedPoints = productSegments.map(segment => {
    const rateDetails = getPrimaryRateDetails(segment?.product);
    const isRedemption = toBoolean(rateDetails?.[0]?.isRedemption);
    return isRedemption ? getExplicitSegmentPoints(segment) : 0;
  });

  const explicitPointTotal = allocatedPoints.reduce((sum, points) => sum + points, 0);
  const itemPointTotal = getItemPoints(item);
  const pointsRemaining = Math.max(itemPointTotal - explicitPointTotal, 0);

  if (pointsRemaining <= 0) {
    return allocatedPoints;
  }
  const segmentsWithoutPoints = redemptionIndexes.filter(index => allocatedPoints[index] <= 0);

  if (!segmentsWithoutPoints.length) {
    return allocatedPoints;
  }

  const basePoints = Math.floor(pointsRemaining / segmentsWithoutPoints.length);
  let remainder = pointsRemaining - basePoints * segmentsWithoutPoints.length;
  segmentsWithoutPoints.forEach(index => {
    allocatedPoints[index] = basePoints;
    if (remainder > 0) {
      allocatedPoints[index] += 1;
      remainder -= 1;
    }
  });
  return allocatedPoints;
};

// Map an order item into its product segments (with normalization)
const mapOrderItemProductToSegments = (item: any) => {
  const productSegments = Array.isArray(item?.productType?.segments) ? item.productType.segments.filter(Boolean) : [];
  if (!productSegments.length) {
    return [mapOrderItemProductToSegment(item, {}, getItemPoints(item))];
  }
  const allocatedPoints = allocatePointsBySegment(item, productSegments);
  return productSegments.map((segment: any, index: number) => {
    return mapOrderItemProductToSegment(item, segment, allocatedPoints[index] ?? 0);
  });
};

type UnknownRecord = Record<string, unknown>;

type BrowserSessionWindow = Window & {
  sessionData?: UnknownRecord;
  sessionDataClient?: UnknownRecord;
};

// Type guard to determine if a value is a plain object (not an array)
const isRecord = (value: unknown): value is UnknownRecord => {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
};

// Normalize property id to an uppercase non-empty string
const normalizePropertyId = (value: unknown): string => {
  return toNonEmptyString(value).toUpperCase();
};

// Read migration status header/value from a headers-like object or record
const getMigrationStatusFromValue = (value: unknown): string => {
  if (!isRecord(value)) {
    return '';
  }

  const directStatus = toNonEmptyString(value[constants.UXL_MIGRATION_STATUS]);

  if (directStatus) {
    return directStatus;
  }

  const headers = value['headers'];

  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    return toNonEmptyString(headers.get(constants.UXL_MIGRATION_STATUS));
  }

  if (isRecord(headers)) {
    return toNonEmptyString(headers[constants.UXL_MIGRATION_STATUS]);
  }

  return '';
};

// Safely drill into session data and return the `AriesReservation` object if present
const getAriesReservation = (sessionData: unknown): UnknownRecord => {
  return (() => {
    if (!isRecord(sessionData)) {
      return {};
    }
    const cacheData = sessionData['cacheData'];
    if (!isRecord(cacheData)) {
      return {};
    }
    const data = cacheData['data'];
    if (!isRecord(data)) {
      return {};
    }
    const reservation = data['AriesReservation'];
    return isRecord(reservation) ? reservation : {};
  })();
};

// Determine the migration status by inspecting session payloads and headers
const getMigrationStatusFromSession = (sessionData: unknown): string => {
  if (!isRecord(sessionData)) {
    return '';
  }

  const reservationStatus = getMigrationStatusFromValue(getAriesReservation(sessionData));

  if (reservationStatus) {
    return reservationStatus;
  }
  const cacheData = sessionData['cacheData'];
  if (isRecord(cacheData)) {
    const cacheDataStatus = getMigrationStatusFromValue(cacheData);
    if (cacheDataStatus) {
      return cacheDataStatus;
    }
    const data = cacheData['data'];
    const cachePayloadStatus = getMigrationStatusFromValue(data);
    if (cachePayloadStatus) {
      return cachePayloadStatus;
    }
  }

  return getMigrationStatusFromValue(sessionData);
};

// Extract normalized property id from session AriesReservation
const getPropertyIdFromSession = (sessionData: unknown): string => {
  const reservation = getAriesReservation(sessionData);
  return normalizePropertyId(reservation['propertyId']);
};

// Attempt to persist migration status via provided setter, returns success flag
const safelySetMigrationStatus = (
  propertyId: string,
  migrationStatus: string,
  storeMigrationStatus?: UpdateCancelModalBookPageContextParams['setUXLMigrationStatus']
): boolean => {
  if (!propertyId || !migrationStatus || !canUseDOM || !storeMigrationStatus) {
    return false;
  }
  try {
    const headers = new Headers();
    headers.set(constants.UXL_MIGRATION_STATUS, migrationStatus);

    storeMigrationStatus(headers, propertyId);

    return true;
  } catch {
    return false;
  }
};

// Return an updated session object with AriesReservation fields set/merged
const updateSessionReservation = (
  sessionData: unknown,
  propertyId: string,
  tripId: string,
  migrationStatus: string,
  requestedBy: unknown
): UnknownRecord => {
  const normalizedSession = isRecord(sessionData) ? sessionData : {};
  const cacheData = isRecord(normalizedSession['cacheData']) ? normalizedSession['cacheData'] : {};
  const cachePayload = isRecord(cacheData['data']) ? cacheData['data'] : {};
  const existingReservation = isRecord(cachePayload['AriesReservation']) ? cachePayload['AriesReservation'] : {};
  return {
    ...normalizedSession,
    cacheData: {
      ...cacheData,
      data: {
        ...cachePayload,
        AriesReservation: {
          ...existingReservation,
          ...(propertyId ? { propertyId } : {}),
          ...(tripId ? { tripId } : {}),
          ...(requestedBy ? { tripsXRequestedByHeader: requestedBy } : {}),
          ...(migrationStatus
            ? {
                uxlMigrationStatus: migrationStatus,
                [constants.UXL_MIGRATION_STATUS]: migrationStatus,
              }
            : {}),
        },
      },
    },
  };
};
// Normalize booking segments for downstream UI usage
const normalizeBookingSegments = (bookingItem: TripBookingDataInfo) => {
  const segments = Array.isArray((bookingItem as any)?.productType?.segments)
    ? (bookingItem as any).productType.segments
    : [];

  if (!segments.length) {
    const isRedemption = Boolean((bookingItem?.basicInformation as any)?.isRedemption);
    return [
      {
        product: {
          basicInformation: {
            startDate: bookingItem?.basicInformation?.startDate ?? '',
            endDate: bookingItem?.basicInformation?.endDate ?? '',
            name: '',
            description: '',
            isRedemption,
            lengthOfStay: bookingItem?.basicInformation?.lengthOfStay ?? 0,
          },
          rate: {
            name: '',
            amount: {
              decimalPoint: 0,
              currency: '',
              amount: 0,
            },
          },
          termsAndConditions: {
            rules: [],
            freeCancellationUntil: '',
          },
          rateDetails: {
            id: '',
            name: '',
            displayRateName: '',
            ratePlanCategory: '',
            isRedemption,
            isFreeNight: false,
            isFna: false,
            mappedPoints: 0,
            points: 0,
            perNightAmount: {
              decimalPoint: 0,
              currency: '',
              amount: 0,
            },
            roomTotalAmount: {
              decimalPoint: 0,
              currency: '',
              amount: 0,
            },
          },
          totalPricing: {
            points: 0,
            total: {
              decimalPoint: 0,
              currency: '',
              amount: 0,
            },
            taxes: {
              decimalPoint: 0,
              currency: '',
              amount: 0,
            },
            subtotal: {
              decimalPoint: 0,
              currency: '',
              amount: 0,
            },
          },
        },
      },
    ];
  }
  return segments.map((segment: any) => {
    const product = segment?.product ?? {};
    const rateDetails = getPrimaryRateDetails(product);
    const termsAndConditions = product?.termsAndConditions ?? {};
    const productBasicInformation = product?.basicInformation ?? {};
    const productRate = product?.rate ?? {};
    const productTotalPricing = product?.totalPricing ?? {};
    const normalizedPolicyRules = (Array.isArray(termsAndConditions?.rules) ? termsAndConditions.rules : []).map(
      (rule: any) => {
        const descriptions =
          Array.isArray(rule?.descriptions) && rule.descriptions.length
            ? rule.descriptions.filter(Boolean)
            : (Array.isArray(rule?.localizedDescriptions) ? rule.localizedDescriptions : [])
                .map((localizedDescription: any) => localizedDescription?.translatedText)
                .filter((text: unknown): text is string => typeof text === 'string' && Boolean(text.trim()));
        return {
          ...rule,
          descriptions,
        };
      }
    );

    const normalizedRateName = getRateName(product);
    const isRedemption = Boolean(rateDetails?.[0]?.isRedemption);
    const mappedPoints = isRedemption
      ? toNumber(
          rateDetails?.[0]?.mappedPoints ??
            rateDetails?.[0]?.points ??
            productTotalPricing?.points ??
            productRate?.amount?.amount
        )
      : 0;

    const perNightAmount = rateDetails?.[0]?.perNightAmount ??
      productRate?.amount ?? {
        decimalPoint: 0,
        currency: '',
        amount: 0,
      };

    return {
      ...segment,
      product: {
        ...product,
        basicInformation: {
          ...productBasicInformation,
          startDate:
            productBasicInformation?.startDate ||
            rateDetails?.[0]?.startDate ||
            bookingItem?.basicInformation?.startDate ||
            '',
          endDate: productBasicInformation?.endDate || bookingItem?.basicInformation?.endDate || '',
          name: productBasicInformation?.name || '',
          description: productBasicInformation?.description || '',
          isRedemption,
          lengthOfStay: toNumber(
            productBasicInformation?.lengthOfStay ??
              rateDetails?.[0]?.lengthOfStay ??
              bookingItem?.basicInformation?.lengthOfStay
          ),
        },
        rate: {
          ...productRate,
          name: normalizedRateName,
          amount: {
            ...(perNightAmount ?? {}),
            amount: toNumber(perNightAmount?.amount),
          },
        },
        termsAndConditions: {
          ...termsAndConditions,
          rules: normalizedPolicyRules,
          freeCancellationUntil: termsAndConditions?.freeCancellationUntil || '',
        },
        rateDetails: {
          ...rateDetails,
          id: rateDetails?.[0]?.id || '',
          name: normalizedRateName,
          displayRateName: normalizedRateName,
          ratePlanCategory: rateDetails?.[0]?.ratePlanCategory || rateDetails?.[0]?.ratePlanType || '',
          isRedemption,
          isFreeNight: Boolean(rateDetails?.[0]?.isFreeNight),
          isFna: Boolean(rateDetails?.[0]?.isFna ?? rateDetails?.[0]?.isFreeNight),
          mappedPoints,
          points: mappedPoints,
          isPointsFreeNightsPoints: Boolean(rateDetails?.[0]?.isPointsFreeNightsPoints),
          perNightAmount: {
            ...(perNightAmount ?? {}),
            amount: toNumber(perNightAmount?.amount),
          },
        },
        totalPricing: {
          ...productTotalPricing,
          points: mappedPoints,
          total: {
            ...(productTotalPricing?.total ?? {}),
            amount: toNumber(productTotalPricing?.total?.amount),
          },
          taxes: {
            ...(productTotalPricing?.taxes ?? {}),
            amount: toNumber(productTotalPricing?.taxes?.amount),
          },
          subtotal: {
            ...(productTotalPricing?.subtotal ?? {}),
            amount: toNumber(productTotalPricing?.subtotal?.amount),
          },
        },
      },
    };
  });
};

// Build a compact segment summary used in the summary of charges
const buildSegmentData = (segments: any[] = []) =>
  segments.map(segment => {
    const rateDetails = getPrimaryRateDetails(segment?.product);
    const isRedemption = Boolean(rateDetails?.[0]?.isRedemption);
    const points = isRedemption
      ? toNumber(
          rateDetails?.[0]?.mappedPoints ??
            rateDetails?.[0]?.points ??
            segment?.product?.totalPricing?.points ??
            segment?.product?.rate?.amount?.amount
        )
      : 0;

    return {
      isFNA: Boolean(rateDetails?.[0]?.isFna ?? rateDetails?.[0]?.isFNA ?? rateDetails?.[0]?.isFreeNight),
      isRedemptionNight: isRedemption,
      showCash: !isRedemption,
      // may be provided by rateDetails, default false
      suppressRate: Boolean(rateDetails?.[0]?.suppressRate ?? false),
      isFreeNight: Boolean(rateDetails?.[0]?.isFreeNight),
      fnaTopOffPoints: toNumber(rateDetails?.[0]?.fnaTopOffPoints ?? 0),
      startDate: rateDetails?.[0]?.startDate || segment?.product?.basicInformation?.startDate || '',
      showPoints: isRedemption,
      isRedemption,
      points,
      isPointsFreeNightsPoints: Boolean(rateDetails?.[0]?.isPointsFreeNightsPoints),
      rateCode: rateDetails?.[0]?.rateCode ?? rateDetails?.[0]?.code ?? segment?.product?.rate?.code ?? '',
      isCashPlusPoints: Boolean(rateDetails?.[0]?.isCashPlusPoints),
    };
  });

// Map a single segment into a normalized product+pricing object
const mapOrderItemProductToSegment = (item: any, segment: any, allocatedPoints = 0) => {
  const product = segment?.product ?? {};
  const rateDetails = getPrimaryRateDetails(product);
  const termsAndConditions = product?.termsAndConditions ?? {};
  const roomDescriptions = product?.descriptions ?? {};
  const rateName = getRateName(product);
  const perNightAmount = mapMoneyAmount(
    product?.totalRate?.rateAmountsByMode?.average?.subtotal ??
      rateDetails?.[0]?.perNightAmount ??
      product?.rate?.amount
  );
  const itemTotalAmount = mapMoneyAmount(product?.totalPricing?.total);
  const itemTaxAmount = mapMoneyAmount(product?.totalPricing?.taxes);
  const isRedemption = toBoolean(rateDetails?.[0]?.isRedemption);
  const pointsValue = isRedemption ? toNumber(allocatedPoints) : 0;
  const lengthOfStay = toNumber(rateDetails?.[0]?.lengthOfStay ?? item?.basicInformation?.lengthOfStay);
  const startDate =
    rateDetails?.[0]?.startDate ?? product?.basicInformation?.startDate ?? item?.basicInformation?.startDate ?? '';
  const endDate =
    product?.basicInformation?.endDate ?? item?.basicInformation?.endDate ?? deriveEndDate(startDate, lengthOfStay);
  const calculatedCashTotal =
    !isRedemption && perNightAmount.amount > 0 ? perNightAmount.amount * Math.max(lengthOfStay, 1) : 0;
  const resolvedCashTotal = itemTotalAmount.amount > 0 ? itemTotalAmount.amount : calculatedCashTotal;
  const resolvedRoomTotal = isRedemption ? 0 : resolvedCashTotal;
  const resolvedTax = itemTaxAmount.amount;
  const resolvedSubtotal = Math.max(resolvedRoomTotal - resolvedTax, 0);
  const currency = itemTotalAmount.currency || perNightAmount.currency || itemTaxAmount.currency || '';
  const decimalPoint = itemTotalAmount.decimalPoint || perNightAmount.decimalPoint || itemTaxAmount.decimalPoint || 0;

  return {
    ...segment,
    product: {
      ...product,
      basicInformation: {
        ...(product?.basicInformation ?? {}),
        startDate,
        endDate,
        name: typeof roomDescriptions?.name === 'string' ? roomDescriptions.name : '',
        description: typeof roomDescriptions?.description === 'string' ? roomDescriptions.description : '',
        isRedemption,
        lengthOfStay,
      },
      rate: {
        ...(product?.rate ?? {}),
        name: rateName,
        amount: {
          ...(product?.rate?.amount ?? {}),
          amount: isRedemption ? pointsValue : perNightAmount.amount,
          currency: isRedemption ? '' : perNightAmount.currency || currency,
          decimalPoint: isRedemption ? 0 : perNightAmount.decimalPoint || decimalPoint,
        },
      },
      termsAndConditions,
      rateDetails: {
        ...rateDetails,
        isRedemption,
        // prefer explicit isFna/isFNA flag, fall back to isFreeNight
        isFna: Boolean(rateDetails?.[0]?.isFna ?? rateDetails?.[0]?.isFNA ?? rateDetails?.[0]?.isFreeNight),
        isFreeNight: Boolean(rateDetails?.[0]?.isFreeNight),
        mappedPoints: pointsValue,
        points: pointsValue,
        isPointsFreeNightsPoints: Boolean(rateDetails?.[0]?.isPointsFreeNightsPoints),
        name: rateName,
        displayRateName: rateName,
        ratePlanCategory: rateDetails?.[0]?.ratePlanCategory ?? '',
        // additional flags used by downstream components
        rateCode: rateDetails?.[0]?.rateCode ?? rateDetails?.[0]?.code ?? product?.rate?.code ?? '',
        suppressRate: Boolean(rateDetails?.[0]?.suppressRate ?? false),
        fnaTopOffPoints: toNumber(rateDetails?.[0]?.fnaTopOffPoints ?? 0),
        isCashPlusPoints: Boolean(rateDetails?.[0]?.isCashPlusPoints),
        perNightAmount: {
          ...(rateDetails?.[0]?.perNightAmount ?? {}),
          amount: perNightAmount.amount,
          currency: perNightAmount.currency || currency,
          decimalPoint: perNightAmount.decimalPoint || decimalPoint,
        },
        roomTotalAmount: {
          ...(rateDetails?.[0]?.roomTotalAmount ?? {}),
          amount: resolvedRoomTotal,
          currency,
          decimalPoint,
        },
      },
      totalPricing: {
        ...(product?.totalPricing ?? {}),
        points: pointsValue,
        total: {
          ...(product?.totalPricing?.total ?? {}),
          amount: resolvedRoomTotal,
          currency,
          decimalPoint,
        },
        taxes: {
          ...(product?.totalPricing?.taxes ?? {}),
          amount: resolvedTax,
          currency: itemTaxAmount.currency || currency,
          decimalPoint: itemTaxAmount.decimalPoint || decimalPoint,
        },
        subtotal: {
          ...(product?.totalPricing?.subtotal ?? {}),
          amount: resolvedSubtotal,
          currency,
          decimalPoint,
        },
      },
    },
  };
};
export const transformOrderResponseToBookingItems = (
  orderResponse: OrderByOrderIdProps | undefined,
  selectedConfirmationNumber?: string
): TripBookingDataInfo[] => {
  const orderByConfirmationNumber = orderResponse?.commerce?.order?.orderByConfirmationNumber;
  const orderItems = orderByConfirmationNumber?.items;

  if (!Array.isArray(orderItems) || !orderItems.length) {
    return [];
  }

  const bookingItems = orderItems
    .map((item: any) => {
      const confirmationNumber = item?.basicInformation?.confirmationNumber ?? selectedConfirmationNumber ?? '';

      if (!confirmationNumber) {
        return null;
      }
      const derivedNuaStatus = item?.nuaInformation?.nuaRequestStatus?.status ?? '';
      const awardRequests = derivedNuaStatus
        ? [
            {
              status: {
                code: derivedNuaStatus,
              },
            },
          ]
        : [];

      const mappedSegments = mapOrderItemProductToSegments(item);
      const firstMappedSegment = mappedSegments[0];
      const mappedBasicInformation = firstMappedSegment?.product?.basicInformation ?? {};
      const orderItemId = item?.id ?? '';
      const isRedemption = mappedSegments.some((segment: any) => Boolean(segment?.product?.rateDetails?.isRedemption));
      const totalPoints = mappedSegments.reduce((sum: number, segment: any) => {
        return (
          sum +
          toNumber(
            segment?.product?.rateDetails?.mappedPoints ??
              segment?.product?.rateDetails?.points ??
              segment?.product?.totalPricing?.points
          )
        );
      }, 0);
      const fallbackTotalPoints = getItemPoints(item);
      const resolvedTotalPoints = totalPoints > 0 ? totalPoints : fallbackTotalPoints;
      const notifications = normalizeNotifications(item?.notifications);
      return {
        id: orderItemId,
        orderId: item?.orderId ?? '',
        basicInformation: {
          confirmationNumber,
          startDate: item?.basicInformation?.startDate ?? mappedBasicInformation?.startDate ?? '',
          endDate: item?.basicInformation?.endDate ?? mappedBasicInformation?.endDate ?? '',
          lengthOfStay:
            item?.basicInformation?.lengthOfStay ??
            mappedSegments.reduce((sum: number, segment: any) => {
              return sum + toNumber(segment?.product?.basicInformation?.lengthOfStay);
            }, 0),
          isRedemption,
          isModifiable: Boolean(item?.basicInformation?.isModifiable),
          isCancellable: item?.basicInformation?.isCancellable ?? true,
          creatorId: item?.basicInformation?.creatorId ?? '',
        },
        notifications,
        totalPricing: {
          ...(item?.totalPricing ?? {}),
          points: resolvedTotalPoints,
        },
        property: {
          id: item?.property?.id ?? '',
          basicInformation: {
            gmtOffset: item?.property?.basicInformation?.gmtOffset ?? '',
          },
        },
        nuaInformation: {
          ...(item?.nuaInformation ?? {}),
          nuaRequestStatus: {
            ...(item?.nuaInformation?.nuaRequestStatus ?? {}),
            status: derivedNuaStatus,
          },
        },
        awardRequests,
        productType: {
          segments: mappedSegments,
        },
      } as TripBookingDataInfo;
    })
    .filter(Boolean) as TripBookingDataInfo[];

  return bookingItems;
};

export const syncSessionDataForCancelRoomModal = (
  bookingInfo: TripBookingDataInfo | null | undefined,
  accountSessionData?: UpdateCancelModalBookPageContextParams['accountSessionData'],
  migrationStatusFromResponse = '',
  storeMigrationStatus?: UpdateCancelModalBookPageContextParams['setUXLMigrationStatus']
): boolean => {
  if (!canUseDOM || !bookingInfo) {
    return false;
  }

  const browserWindow = window as BrowserSessionWindow;
  const currentSession = browserWindow.sessionDataClient ?? browserWindow.sessionData ?? {};
  const currentReservation = getAriesReservation(currentSession);
  const accountReservation = getAriesReservation(accountSessionData);
  const bookingPropertyId = normalizePropertyId(bookingInfo?.property?.id);
  const currentSessionPropertyId = getPropertyIdFromSession(currentSession);
  const accountSessionPropertyId = getPropertyIdFromSession(accountSessionData);
  const resolvedPropertyId = bookingPropertyId || currentSessionPropertyId || accountSessionPropertyId;
  const tripId = bookingInfo?.basicInformation?.confirmationNumber?.toString?.() ?? '';

  if (!resolvedPropertyId && !tripId) {
    return false;
  }

  const requestedBy = currentReservation['tripsXRequestedByHeader'] ?? accountReservation['tripsXRequestedByHeader'];
  const responseMigrationStatus = toNonEmptyString(migrationStatusFromResponse);
  const currentSessionMigrationStatus = getMigrationStatusFromSession(currentSession);
  const accountSessionMigrationStatus = getMigrationStatusFromSession(accountSessionData);
  const resolvedMigrationStatus =
    responseMigrationStatus || accountSessionMigrationStatus || currentSessionMigrationStatus;
  const updatedSession = updateSessionReservation(
    currentSession,
    resolvedPropertyId,
    tripId,
    resolvedMigrationStatus,
    requestedBy
  );
  browserWindow.sessionDataClient = updatedSession;
  if (browserWindow.sessionData) {
    browserWindow.sessionData = updateSessionReservation(
      browserWindow.sessionData,
      resolvedPropertyId,
      tripId,
      resolvedMigrationStatus,
      requestedBy
    );
  }
  if (!resolvedPropertyId) {
    return false;
  }
  if (resolvedMigrationStatus && typeof storeMigrationStatus === 'function') {
    safelySetMigrationStatus(resolvedPropertyId, resolvedMigrationStatus, storeMigrationStatus);
  }
  return true;
};

export const updateCancelModalBookPageContext = ({
  orderResponse,
  selectedConfirmationNumber,
  setBookPageContext,
  bookPageState = {},
  bookPageContextComponentIds,
  memberGivenName = '',
  sessionFirstName = '',
  propertyGmtOffset = '',
  accountSessionData,
  setUXLMigrationStatus,
  migrationStatusFromResponse = '',
}: UpdateCancelModalBookPageContextParams): boolean => {
  const summaryOfChargesComponentId = bookPageContextComponentIds?.SUMMARY_OF_CHARGES;
  const roomOverviewComponentId = bookPageContextComponentIds?.ROOM_OVERVIEW;
  const orderDetailsComponentId = BOOK_PAGE_CONTEXT_COMPONENT_IDS.ORDER_DETAILS;
  const orderByConfirmationId = orderResponse?.commerce?.order?.orderByConfirmationNumber?.id ?? '';
  if (!summaryOfChargesComponentId || !roomOverviewComponentId || !setBookPageContext || !selectedConfirmationNumber) {
    return false;
  }
  const allBookingItems = transformOrderResponseToBookingItems(orderResponse, selectedConfirmationNumber);
  if (!allBookingItems.length) {
    return false;
  }
  const selectedBookingInfo =
    allBookingItems.find(
      item => item?.basicInformation?.confirmationNumber?.toString() === selectedConfirmationNumber.toString()
    ) ?? allBookingItems[0];
  const resolvedPropertyGmtOffset =
    propertyGmtOffset ||
    (selectedBookingInfo as any)?.property?.basicInformation?.gmtOffset ||
    orderResponse?.commerce?.order?.orderByConfirmationNumber?.items?.[0]?.property?.basicInformation?.gmtOffset ||
    '';
  const roomOverviewData = allBookingItems.map(bookingItem => {
    const normalizedSegments = normalizeBookingSegments(bookingItem);
    const bookingItemId = bookingItem?.id ?? '';
    const notifications = normalizeNotifications((bookingItem as any)?.notifications);
    const isRedemption = normalizedSegments.some((segment: any) =>
      Boolean(segment?.product?.rateDetails?.isRedemption)
    );

    return {
      id: bookingItemId,
      basicInformation: {
        confirmationNumber: bookingItem?.basicInformation?.confirmationNumber,
        startDate: bookingItem?.basicInformation?.startDate ?? '',
        endDate: bookingItem?.basicInformation?.endDate ?? '',
        lengthOfStay: bookingItem?.basicInformation?.lengthOfStay ?? 0,
        isRedemption,
        isModifiable: Boolean(bookingItem?.basicInformation?.isModifiable),
        isCancellable: bookingItem?.basicInformation?.isCancellable ?? true,
        creatorId: bookingItem?.basicInformation?.creatorId ?? '',
        id: bookingItemId,
        creationDate: bookingItem?.basicInformation?.startDate ?? '',
      },
      productType: {
        segments: normalizedSegments,
      },
      property: {
        id: bookingItem?.property?.id ?? '',
        basicInformation: {
          gmtOffset: (bookingItem as any)?.property?.basicInformation?.gmtOffset || resolvedPropertyGmtOffset,
        },
      },
      notifications,
      guestList: {
        primaryGuest: {
          name: {
            givenName: memberGivenName || sessionFirstName || '',
          },
        },
      },
      nuaInformation: {
        nuaRequestStatus: {
          status: bookingItem?.nuaInformation?.nuaRequestStatus?.status,
        },
      },
      awardRequests: (bookingItem as any)?.awardRequests ?? [],
      totalPricing: {
        ...((bookingItem as any)?.totalPricing ?? {}),
        points: (() => {
          const mappedPoints = normalizedSegments.reduce((sum: number, segment: any) => {
            return (
              sum +
              toNumber(
                segment?.product?.rateDetails?.mappedPoints ??
                  segment?.product?.rateDetails?.points ??
                  segment?.product?.totalPricing?.points
              )
            );
          }, 0);

          return mappedPoints > 0 ? mappedPoints : toNumber((bookingItem as any)?.totalPricing?.points);
        })(),
      },
    };
  });

  const summaryDataArr = allBookingItems.map(bookingItem => {
    const normalizedSegments = normalizeBookingSegments(bookingItem);
    const segmentData = buildSegmentData(normalizedSegments);
    const mappedPointRate = normalizedSegments
      .filter((segment: any) => Boolean(segment?.product?.rateDetails?.isRedemption))
      .reduce((sum: number, segment: any) => {
        return (
          sum +
          toNumber(
            segment?.product?.rateDetails?.mappedPoints ??
              segment?.product?.rateDetails?.points ??
              segment?.product?.totalPricing?.points ??
              segment?.product?.rate?.amount?.amount
          )
        );
      }, 0);
    const fallbackPointRate = toNumber((bookingItem as any)?.totalPricing?.points);
    const pointRate = mappedPointRate > 0 ? mappedPointRate : fallbackPointRate;
    const cashRate = normalizedSegments
      .filter(
        (segment: {
          product?: {
            rateDetails?: {
              isRedemption?: boolean;
            };
          };
        }) => !segment?.product?.rateDetails?.isRedemption
      )
      .reduce((sum: number, segment: any) => sum + getPerNightAmount(segment), 0);
    const tax = normalizedSegments.reduce((sum: number, segment: any) => sum + getSegmentTaxAmount(segment), 0);
    const ratePerRoom = normalizedSegments
      .filter((segment: any) => !segment?.product?.rateDetails?.isRedemption)
      .reduce((sum: number, segment: any) => sum + getSegmentTotalAmount(segment), 0);

    return {
      otherFees: [],
      cashRate,
      pointRate,
      ratePerRoom,
      tax,
      segmentData,
      isRedemption: normalizedSegments.some((segment: any) => Boolean(segment?.product?.rateDetails?.isRedemption)),
      confNumber: bookingItem?.basicInformation?.confirmationNumber || '',
    };
  });

  const orderTotalAmount = summaryDataArr.reduce((sum: number, item: any) => sum + toNumber(item?.ratePerRoom), 0);
  const selectedSegments = selectedBookingInfo ? normalizeBookingSegments(selectedBookingInfo) : [];
  const summaryOfChargesState = (bookPageState?.[summaryOfChargesComponentId] || {}) as Record<string, any>;
  const existingCurrency = summaryOfChargesState?.['data']?.currency;
  const selectedCurrency =
    selectedSegments?.[0]?.product?.rateDetails?.perNightAmount?.currency ||
    selectedSegments?.[0]?.product?.rate?.amount?.currency ||
    selectedSegments?.[0]?.product?.totalPricing?.total?.currency ||
    selectedSegments?.[0]?.product?.totalPricing?.taxes?.currency ||
    (typeof existingCurrency === 'string' ? existingCurrency : existingCurrency?.currency) ||
    '';
  const totalCash = orderTotalAmount;
  const cashSubTotal = summaryDataArr.reduce((sum: number, item: any) => sum + toNumber(item?.cashRate), 0);
  const totalTaxes = summaryDataArr.reduce((sum: number, item: any) => sum + toNumber(item?.tax), 0);
  const totalPoint = summaryDataArr.reduce((sum: number, item: any) => sum + toNumber(item?.pointRate), 0);
  const isRedemption = summaryDataArr.some(item => item?.isRedemption);
  const accountSessionReservation = getAriesReservation(accountSessionData);
  const migrationStatusFromAccountSession = getMigrationStatusFromValue(accountSessionReservation);
  const resolvedMigrationStatus = toNonEmptyString(migrationStatusFromResponse) || migrationStatusFromAccountSession;

  syncSessionDataForCancelRoomModal(
    selectedBookingInfo,
    accountSessionData,
    resolvedMigrationStatus,
    setUXLMigrationStatus
  );

  setBookPageContext({
    componentName: summaryOfChargesComponentId,
    newState: {
      ...summaryOfChargesState,
      data: {
        ...(summaryOfChargesState?.['data'] || {}),
        currency: selectedCurrency,
        totalCash,
        cashSubTotal,
        totalTaxes,
        totalPoint,
        totalPointRate: totalPoint,
        pointRate: totalPoint,
        isRedemption,
        totalOtherFeeArr: [],
        dataArr: summaryDataArr,
      },
    },
  });
  setBookPageContext({
    componentName: roomOverviewComponentId,
    newState: {
      ...((bookPageState?.[roomOverviewComponentId] || {}) as Record<string, any>),
      roomOverviewData,
    },
  });
  if (orderByConfirmationId) {
    setBookPageContext({
      componentName: orderDetailsComponentId,
      newState: {
        ...((bookPageState?.[orderDetailsComponentId] || {}) as Record<string, any>),
        id: orderByConfirmationId,
      },
    });
  }
  return true;
};
